import {Device} from '~redux/types/user';

export function isDataImporterDevice(device: Device): boolean {
  return device.type === 'data_importer' || (device.type === undefined && device.id.endsWith('_data_importer'));
}

const VIRTUAL_DOCKER_DEVICE_ID_PATTERN = /-docker-\d+$/;

export function isDockerDevice(device: Device): boolean {
  return device.type === 'docker' || VIRTUAL_DOCKER_DEVICE_ID_PATTERN.test(device.id);
}

export function isNonDockerEdgeDevice(device: Device): boolean {
  return !isDataImporterDevice(device) && !isDockerDevice(device);
}

export function partitionDevicesByUploadSource(devices: Device[]) {
  const manualUploadDevices: Device[] = [];
  const edgeDevices: Device[] = [];

  for (const device of devices) {
    if (isDataImporterDevice(device)) {
      manualUploadDevices.push(device);
    } else {
      edgeDevices.push(device);
    }
  }

  return {manualUploadDevices, edgeDevices};
}

/**
 * The device's alias, or `undefined` when the device is unknown or has no (non-empty) alias.
 */
export function getDeviceAlias(deviceId: string, devices: Device[]): string | undefined {
  return devices.find((device) => device.id === deviceId)?.alias || undefined;
}

/**
 * Human-readable device label: the alias when set, otherwise the raw device id.
 */
export function getDeviceLabel(deviceId: string, devices: Device[]): string {
  return getDeviceAlias(deviceId, devices) ?? deviceId;
}

export interface DeviceSelectOption {
  value: string;
  label: string;
}

export function mapManualUploadDevicesToOptions(
  devices: Device[],
  manuallyUploadedLabel: string,
): DeviceSelectOption[] {
  if (devices.length === 1) {
    return [{value: devices[0].id, label: manuallyUploadedLabel}];
  }

  return devices.map((device) => ({
    value: device.id,
    label: device.alias || device.id,
  }));
}

export function getDataImporterDeviceIds(devices: Device[]): Set<string> {
  return new Set(devices.filter(isDataImporterDevice).map((device) => device.id));
}

export function stripDataImporterDeviceIds(
  deviceIds: string[] | undefined,
  dataImporterIds: Set<string>,
): string[] | undefined {
  if (!deviceIds?.length) {
    return deviceIds;
  }

  const sanitized = deviceIds.filter((id) => !dataImporterIds.has(id));
  return sanitized.length === deviceIds.length ? deviceIds : sanitized.length > 0 ? sanitized : undefined;
}
